import type { ForwardedRef } from 'react';
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { FeatureAppLoader } from '@feature-hub/react';
import type { AssetTypeVideo, FeatureAppMeta } from '../../../types';
import type { VideoPlayerAssetConfig } from '../../utils/getVideoPlayerConfig';
import { getVideoPlayerConfig } from '../../utils/getVideoPlayerConfig';
import { Context } from '../../Context';
import { throttleScroll } from '../../utils/throttleScroll';
import { SYS_BORDER_RADIUS_MD } from '@oneaudi/unified-web-common';

const VIDEO_PLAYER_FA_NAME = 'fa-video-player';
const VIDEO_PLAYER_FA_VERSION = 'v5.5.4';
const VIDEO_PLAYER_FA_ENV = 'prod';
const VIDEO_PLAYER_FA_BASE_URL = `https://${VIDEO_PLAYER_FA_NAME}.cdn.${VIDEO_PLAYER_FA_ENV}.collab.apps.one.audi/${VIDEO_PLAYER_FA_VERSION}`;

export interface VideoWrapperProps {
  preserveVideoWidth?: boolean;
}
const VideoWrapper = styled.div<VideoWrapperProps>`
  width: 100%;
  video {
    border-radius: ${process.env.IS_LEGACY ? '' : SYS_BORDER_RADIUS_MD};
  }
  ${(props) => {
    return `
    height: ${props.preserveVideoWidth ? 'auto' : '100%'};
      `;
  }}
`;
export interface VideoPlayerHandle {
  pause: () => void;
}
interface VideoProps {
  asset: AssetTypeVideo;
  sectionIndex: number;
  meta: FeatureAppMeta;
  ref: ForwardedRef<VideoPlayerHandle>;
}

export const Video = forwardRef<VideoPlayerHandle, VideoProps>(
  ({ asset, sectionIndex, meta }, ref) => {
    const videoPlayerConfig = useMemo(() => getVideoPlayerConfig(asset), [asset]);

    const { videoService, featureAppEnv, loggerService } = useContext(Context);

    const videoRef = useRef<HTMLDivElement>(null);

    const responsiveMediaAsset: VideoPlayerAssetConfig = {
      ...asset,
      src: asset.src,
    };

    if (!responsiveMediaAsset.src) {
      loggerService?.error('No video source found');
      return null;
    }

    useImperativeHandle(ref, () => ({
      pause: () => {
        const video = videoRef.current?.querySelector('video');
        if (!video) return;
        video.pause();
      },
    }));

    const [scrollY, setScrollY] = useState<number>(0);
    const [, setIsInViewport] = useState<boolean | null>(null);
    const [isAutoplay] = useState<boolean>(asset.autoPlay || false);

    const [isPlaying, setIsPlaying] = useState<boolean>(() => {
      return isAutoplay;
    });

    const setVideoEvents = () => {
      const video = videoRef.current?.querySelector('video');
      if (!video) {
        loggerService?.error('video element not found!');
        return;
      }

      video.addEventListener('play', () => {
        setIsPlaying(true);
      });
      video.addEventListener('ended', () => {
        setIsPlaying(false);
      });
    };

    useEffect(() => {
      // skip if ssr
      /* istanbul ignore next */
      if (!document || !window) {
        return () => {};
      }

      // execute `onScroll` max. every 100 milliseconds

      const onScroll = throttleScroll(() => {
        setScrollY(window.scrollY);
      }, 100) as () => void;

      document.addEventListener('scroll', onScroll);

      return () => {
        document.removeEventListener('scroll', onScroll);
      };
    }, []);

    const videoPlayerId = useMemo(
      () => `${featureAppEnv.featureAppId}_${VIDEO_PLAYER_FA_NAME}`,
      [featureAppEnv, VIDEO_PLAYER_FA_NAME],
    );

    useEffect(() => {
      // skip ssr and loading state
      if (videoRef.current === null || !window || !videoService) {
        return;
      }

      const rect = videoRef.current.getBoundingClientRect();
      const outTop = rect.top + rect.height < 0;
      const outBottom = rect.top > window.innerHeight;
      const isInViewport_ = !outTop && !outBottom;

      setIsInViewport((wasInViewport) => {
        // case: initial value => return first "real" value
        // and don't interfere with video settings like `autoplay`
        /* istanbul ignore next */
        if (wasInViewport === null) {
          return isInViewport_;
        }

        // https://github.com/testing-library/react-testing-library/issues/671
        /* istanbul ignore next */
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        (async (): Promise<void> => {
          try {
            // case: video scrolls out
            if (wasInViewport && !isInViewport_ && isPlaying) {
              videoService.stop(videoPlayerId);
              setIsPlaying(false);
              setTimeout(() => {
                const videoElement = videoRef.current?.querySelector('video');
                if (videoElement && videoElement.currentTime >= 0) {
                  videoElement.currentTime = 0;
                }
              }, 200);
            }

            // case: video scrolls in
            if (!wasInViewport && isInViewport_ && isAutoplay) {
              await videoService.play(videoPlayerId);
            }
          } catch {
            // play failed due to browser policies:
            // Safari: https://developer.apple.com/documentation/webkit/delivering_video_content_for_safari
            // Chrome: https://goo.gl/xX8pDD
          }
        })();

        /* istanbul ignore next */
        return isInViewport_;
      });
    }, [isPlaying, isAutoplay, scrollY, videoService, videoPlayerId]);

    const alpha = process.env.IS_LEGACY ? '' : '-alpha';

    return (
      <VideoWrapper
        onLoad={setVideoEvents}
        ref={videoRef}
        preserveVideoWidth={asset.preserveVideoWidth}
      >
        <FeatureAppLoader
          featureAppId={`${meta.featureAppId}_${VIDEO_PLAYER_FA_NAME}-tab-${sectionIndex}`}
          featureAppName={VIDEO_PLAYER_FA_NAME}
          config={videoPlayerConfig}
          serverSrc={`${VIDEO_PLAYER_FA_BASE_URL}/fh/app${alpha}.node.js`}
          src={`${VIDEO_PLAYER_FA_BASE_URL}/fh/app${alpha}.js`}
        />
      </VideoWrapper>
    );
  },
);
